import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useI18n } from '../i18n';

const FRAMES_PER_FOLDER = 77;
const TOTAL_STAGES = 5;
const ANIMATION_FOLDERS = ['1-2', '2-3', '3-4', '4-5'];
const SCROLL_SCREENS_PER_STAGE = 0.5;
const EXTRA_SCROLL_SCREENS = 0.15;

const SECTION_HEIGHT = `${(TOTAL_STAGES * SCROLL_SCREENS_PER_STAGE + EXTRA_SCROLL_SCREENS) * 100}vh`;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function fitImageContain(
  canvasWidth: number,
  canvasHeight: number,
  imageWidth: number,
  imageHeight: number
) {
  const scale = Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  return {
    width,
    height,
    x: (canvasWidth - width) / 2,
    y: (canvasHeight - height) / 2,
  };
}

export default function ProcessFramesSection() {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const imagesRef = useRef<Record<string, HTMLImageElement | null>>({});
  const currentFrameRef = useRef(0);
  const currentFolderRef = useRef(ANIMATION_FOLDERS[0]);
  const currentStageRef = useRef(0);
  const scrollRafRef = useRef<number | null>(null);
  const playbackRafRef = useRef<number | null>(null);
  const prefersReducedMotion = useReducedMotion();

  const [loadedFrames, setLoadedFrames] = useState(0);
  const [activeStage, setActiveStage] = useState(0);
  const stages = [
    { number: '01', title: t('process.1.title'), description: t('process.1.desc') },
    { number: '02', title: t('process.2.title'), description: t('process.2.desc') },
    { number: '03', title: t('process.3.title'), description: t('process.3.desc') },
    { number: '04', title: t('process.4.title'), description: t('process.4.desc') },
    { number: '05', title: t('process.5.title'), description: t('process.5.desc') },
  ];

  const totalFrames = ANIMATION_FOLDERS.length * FRAMES_PER_FOLDER;
  const isLoaded = loadedFrames >= totalFrames;

  const getImageKey = (folder: string, frameNum: number) => `${folder}-${frameNum}`;

  const getLoadedImage = useCallback((folder: string, frameNum: number): HTMLImageElement | null => {
    const key = getImageKey(folder, frameNum);
    if (imagesRef.current[key]) return imagesRef.current[key];
    for (let offset = 1; offset < FRAMES_PER_FOLDER; offset++) {
      const prev = imagesRef.current[getImageKey(folder, frameNum - offset)];
      const next = imagesRef.current[getImageKey(folder, frameNum + offset)];
      if (prev) return prev;
      if (next) return next;
    }
    return null;
  }, []);

  const drawFrame = useCallback(
    (folder: string, frameNum: number) => {
      const canvas = canvasRef.current;
      const image = getLoadedImage(folder, frameNum);
      if (!canvas || !image) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const bounds = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.floor(bounds.width * dpr));
      const height = Math.max(1, Math.floor(bounds.height * dpr));

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);

      const frame = fitImageContain(width, height, image.naturalWidth, image.naturalHeight);
      ctx.drawImage(image, frame.x, frame.y, frame.width, frame.height);
      currentFrameRef.current = frameNum;
    },
    [getLoadedImage]
  );

  const stopPlayback = useCallback(() => {
    if (playbackRafRef.current !== null) {
      window.cancelAnimationFrame(playbackRafRef.current);
      playbackRafRef.current = null;
    }
  }, []);

  const animateToFrame = useCallback(
    (folder: string, targetFrame: number) => {
      stopPlayback();
      if (prefersReducedMotion) {
        drawFrame(folder, targetFrame);
        return;
      }

      const startFrame = currentFrameRef.current;
      if (startFrame === targetFrame) {
        drawFrame(folder, targetFrame);
        return;
      }

      const distance = Math.abs(targetFrame - startFrame);
      const duration = clamp(distance * 16, 400, 1400);
      const startTime = performance.now();

      const tick = (timestamp: number) => {
        const elapsed = timestamp - startTime;
        const progress = clamp(elapsed / duration, 0, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const nextFrame = Math.round(startFrame + (targetFrame - startFrame) * eased);

        if (nextFrame !== currentFrameRef.current) drawFrame(folder, nextFrame);

        if (progress < 1) {
          playbackRafRef.current = window.requestAnimationFrame(tick);
          return;
        }

        playbackRafRef.current = null;
        drawFrame(folder, targetFrame);
      };

      playbackRafRef.current = window.requestAnimationFrame(tick);
    },
    [drawFrame, prefersReducedMotion, stopPlayback]
  );

  const transitionToStage = useCallback(
    (stageIndex: number) => {
      if (stageIndex === currentStageRef.current) return;

      const prevStage = currentStageRef.current;
      currentStageRef.current = stageIndex;
      setActiveStage(stageIndex);

      if (stageIndex === 0) {
        if (prevStage > 0) {
          const folder = ANIMATION_FOLDERS[prevStage - 1];
          currentFolderRef.current = folder;
          currentFrameRef.current = FRAMES_PER_FOLDER;
          animateToFrame(folder, 1);
        } else {
          currentFolderRef.current = ANIMATION_FOLDERS[0];
          currentFrameRef.current = 1;
          drawFrame(ANIMATION_FOLDERS[0], 1);
        }
      } else if (stageIndex > 0 && stageIndex < TOTAL_STAGES) {
        const folder = ANIMATION_FOLDERS[stageIndex - 1];
        currentFolderRef.current = folder;
        const isDown = stageIndex > prevStage;
        const targetFrame = isDown ? FRAMES_PER_FOLDER : 1;
        const startFrame = isDown ? 1 : FRAMES_PER_FOLDER;
        currentFrameRef.current = startFrame;
        animateToFrame(folder, targetFrame);
      }
    },
    [animateToFrame, drawFrame]
  );

  useEffect(() => {
    let cancelled = false;
    let completed = 0;

    ANIMATION_FOLDERS.forEach((folder) => {
      for (let i = 1; i <= FRAMES_PER_FOLDER; i++) {
        const image = new Image();
        image.decoding = 'async';
        const frameNum = String(i).padStart(3, '0');

        image.onload = () => {
          if (cancelled) return;
          imagesRef.current[getImageKey(folder, i)] = image;
          completed++;
          setLoadedFrames(completed);
          if (completed === 1) drawFrame(ANIMATION_FOLDERS[0], 1);
        };

        image.onerror = () => {
          if (cancelled) return;
          completed++;
          setLoadedFrames(completed);
        };

        image.src = `/animation_1/${folder}/ezgif-frame-${frameNum}.jpg`;
      }
    });

    return () => { cancelled = true; };
  }, [drawFrame]);

  useEffect(() => {
    const updateFromScroll = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const scrollableDistance = Math.max(1, rect.height - window.innerHeight);
      const scrollProgress = clamp(-rect.top / scrollableDistance, 0, 1);
      const stageProgress = scrollProgress * TOTAL_STAGES;
      const stageIndex = Math.min(TOTAL_STAGES - 1, Math.floor(stageProgress));
      if (stageIndex !== currentStageRef.current) transitionToStage(stageIndex);
    };

    const requestTick = () => {
      if (scrollRafRef.current !== null) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;
        updateFromScroll();
      });
    };

    updateFromScroll();
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick);

    return () => {
      window.removeEventListener('scroll', requestTick);
      window.removeEventListener('resize', requestTick);
      if (scrollRafRef.current !== null) window.cancelAnimationFrame(scrollRafRef.current);
      stopPlayback();
    };
  }, [transitionToStage, stopPlayback]);

  const currentStage = stages[activeStage];
  const loadingProgress = Math.round((loadedFrames / totalFrames) * 100);

  return (
    <section
      id="process"
      ref={sectionRef}
      className="relative w-full bg-black"
      style={{ height: SECTION_HEIGHT }}
    >
      {/* Mobile: static stage cards — shown via CSS (height:auto override in index.css) */}
      <div className="md:hidden px-6 py-10">
        <div className="mb-8 text-[11px] font-medium tracking-[0.22em] uppercase text-white/25">
          {t('process.badgeMobile')}
        </div>
        <div className="space-y-4">
          {stages.map((stage, i) => (
            <motion.div
              key={stage.number}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="rounded-2xl px-5 py-5"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <div
                className="mb-2 font-extrabold leading-none"
                style={{
                  fontSize: '2rem',
                  letterSpacing: '-0.03em',
                  background: 'linear-gradient(135deg, #60A5FA 0%, #818CF8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {stage.number}
              </div>
              <h3 className="text-white font-bold text-lg mb-1 tracking-tight">{stage.title}</h3>
              <p className="text-white/40 text-sm leading-relaxed">{stage.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Desktop: scroll-driven canvas animation (absolute overlay, hidden on mobile) */}
      <div className="hidden md:block" style={{ position: 'absolute', inset: 0 }}>
        <div className="absolute inset-x-0 top-0 h-32 pointer-events-none z-20" style={{ background: 'linear-gradient(to bottom, #000 0%, transparent 100%)' }} />
        <div className="absolute inset-x-0 bottom-0 h-32 pointer-events-none z-20" style={{ background: 'linear-gradient(to top, #000 0%, transparent 100%)' }} />
        <div className="sticky top-0 h-screen overflow-hidden bg-black">
          <div className="relative h-full w-full flex">
            <div className="relative z-10 flex flex-col justify-center pl-10 md:pl-16 lg:pl-24 pr-8 w-full md:w-2/5 lg:w-[38%] pointer-events-none">
              <div className="mb-10 text-[11px] font-medium tracking-[0.22em] uppercase text-white/25">
                {t('process.badgeDesktop')}
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStage.number}
                  initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -10 }}
                  transition={{ duration: prefersReducedMotion ? 0.01 : 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <div
                    className="mb-4 font-extrabold leading-none"
                    style={{
                      fontSize: 'clamp(4rem, 6vw, 6rem)',
                      letterSpacing: '-0.03em',
                      background: 'linear-gradient(135deg, #60A5FA 0%, #818CF8 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {currentStage.number}
                  </div>
                  <h2 className="mb-4 text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight" style={{ letterSpacing: '-0.025em' }}>
                    {currentStage.title}
                  </h2>
                  <p className="text-base leading-relaxed text-white/40 max-w-[300px]">
                    {currentStage.description}
                  </p>
                </motion.div>
              </AnimatePresence>
              <div className="mt-10 flex items-center gap-3">
                {stages.map((stage, index) => (
                  <motion.div
                    key={stage.number}
                    className="rounded-full"
                    animate={{
                      width: index === activeStage ? 28 : 6,
                      height: 3,
                      backgroundColor:
                        index === activeStage
                          ? '#60A5FA'
                          : index < activeStage
                          ? 'rgba(96, 165, 250, 0.35)'
                          : 'rgba(255, 255, 255, 0.1)',
                    }}
                    transition={{ duration: prefersReducedMotion ? 0.01 : 0.3, ease: 'easeOut' }}
                  />
                ))}
              </div>
            </div>
            <div className="absolute inset-0 md:relative md:flex-1 flex items-center justify-center">
              <canvas
                ref={canvasRef}
                className="h-full w-full"
                style={{ objectFit: 'contain' }}
                aria-label={t('process.aria')}
              />
            </div>
          </div>
          {!isLoaded && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black">
              <div className="text-center">
                <div className="mb-3 text-[10px] uppercase tracking-[0.25em] text-white/30">{t('process.loading')}</div>
                <div className="text-2xl font-bold text-white/60">{loadingProgress}%</div>
                <div className="mt-5 h-px w-40 bg-white/10 overflow-hidden rounded-full">
                  <motion.div
                    className="h-full bg-blue-400/60"
                    style={{ width: `${loadingProgress}%` }}
                    transition={{ ease: 'easeOut' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
